import React, { useState, useEffect } from 'react';
import { Shield, KeyRound, UserCheck, Lock, AlertCircle, Loader2, Sparkles, Server } from 'lucide-react';
import { adminApi, setAdminSession } from '../services/api.js';
import type { AdminAccountDto } from '@nova-mine/shared';

interface LoginModalProps {
  onSuccess: (admin: AdminAccountDto) => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ onSuccess }) => {
  const [isBootstrapped, setIsBootstrapped] = useState<boolean | null>(null);
  const [isBootstrapMode, setIsBootstrapMode] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [secretKey, setSecretKey] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checkingBootstrap, setCheckingBootstrap] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function checkStatus() {
      try {
        setCheckingBootstrap(true);
        const res = await adminApi.getBootstrapStatus();
        setIsBootstrapped(res.is_bootstrapped);
        if (!res.is_bootstrapped) {
          setIsBootstrapMode(true);
        }
      } catch (err) {
        console.warn('[Bootstrap Status Check Failed]', err);
        setIsBootstrapped(true); // Fallback to normal login
      } finally {
        setCheckingBootstrap(false);
      }
    }
    checkStatus();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError(null);

      const res = await adminApi.login({ username, password });
      onSuccess(res.admin);
    } catch (err: unknown) {
      console.error('[Admin Login Error]', err);
      setError(err instanceof Error ? err.message : 'Invalid credentials or access denied');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBootstrap = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const res = await adminApi.bootstrap({
        username,
        password,
        bootstrap_secret: secretKey || undefined,
      });

      onSuccess(res.admin);
    } catch (err: unknown) {
      console.error('[Admin Bootstrap Error]', err);
      setError(err instanceof Error ? err.message : 'System initialization failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingBootstrap) {
    return (
      <div className="min-h-screen bg-space-950 flex items-center justify-center p-4">
        <div className="glass-panel p-8 rounded-3xl text-center max-w-sm w-full border border-white/10">
          <Loader2 className="w-8 h-8 animate-spin text-nova-cyan mx-auto mb-3" />
          <h3 className="text-sm font-bold text-white">Connecting to Gateway...</h3>
          <p className="text-xs text-gray-400 mt-1">Verifying administrator security posture</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-space-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/2 -translate-x-1/2 translate-y-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-md w-full glass-panel-glow rounded-3xl p-8 border border-white/15 relative z-10 shadow-2xl">
        {/* Header Branding */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-purple-600/30 to-cyan-500/30 border border-white/20 flex items-center justify-center text-nova-cyan mx-auto mb-4 shadow-xl shadow-cyan-500/10">
            <Shield className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-black text-white flex items-center justify-center space-x-1.5">
            <span>NOVA MINE</span>
            <span className="text-nova-gold text-xs px-2 py-0.5 rounded-full bg-amber-500/10 border border-nova-gold/30">
              VIP ADMIN
            </span>
          </h2>
          <p className="text-xs text-gray-400 mt-1.5">
            {isBootstrapMode
              ? 'One-Time Initialization: Create the first Superadmin account'
              : 'Secure Administrative Command Gateway'}
          </p>
        </div>

        {/* Mode Switcher if unbootstrapped */}
        {isBootstrapped === false && (
          <div className="mb-6 p-1 bg-space-900 rounded-xl border border-white/10 flex">
            <button
              type="button"
              onClick={() => {
                setIsBootstrapMode(true);
                setError(null);
              }}
              className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                isBootstrapMode
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Initial Bootstrap
            </button>
            <button
              type="button"
              onClick={() => {
                setIsBootstrapMode(false);
                setError(null);
              }}
              className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                !isBootstrapMode
                  ? 'bg-space-800 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Sign In
            </button>
          </div>
        )}

        {/* Error Notification */}
        {error && (
          <div className="mb-5 p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start space-x-2.5 text-xs text-red-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1 font-medium">{error}</div>
          </div>
        )}

        {/* Forms */}
        {isBootstrapMode ? (
          <form onSubmit={handleBootstrap} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1.5">
                Superadmin Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. root_admin"
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1.5">
                Password (min 8 characters)
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a strong passphrase..."
                required
                minLength={8}
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1.5">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your passphrase..."
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1.5 flex justify-between">
                <span>Optional Bootstrap Secret</span>
                <span className="text-[10px] text-gray-500">ADMIN_API_KEY if configured</span>
              </label>
              <input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="Optional security key..."
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors font-mono"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="mt-6 w-full py-3.5 rounded-xl font-bold text-xs bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-500 text-white shadow-xl shadow-purple-600/25 hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Initializing Admin System...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Create Superadmin Account</span>
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1.5">
                Admin Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your admin username..."
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1.5">
                Passphrase
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your security passphrase..."
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors"
              />
            </div>

            <div className="pt-2 text-[11px] text-gray-400 flex items-center space-x-1.5">
              <Lock className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
              <span>Sessions expire automatically after 24 hours of inactivity.</span>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="mt-6 w-full py-3.5 rounded-xl font-bold text-xs bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 text-white shadow-xl shadow-cyan-500/25 hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Authenticating Session...</span>
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4" />
                  <span>Sign In to Admin Console</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
